<template>
	<transition
		name="modal"
		class="sm"
		appear
		appear-active-class="modal-enter-active"
	>
		<section class="global-modal" @click="$emit('close')">
			<section
				class="modal-dialog"
				role="document"
				ref="modalDialog"
				@click.stop
			>
				<section class="modal-wrapper" data-test="modal-wrapper">
					<header class="modal-header">
						<button
							v-if="showBackCaret"
							type="button"
							class="b360-modal-back-btn"
							data-test="back-button"
							aria-label="Back Button"
							@click="$emit('back')"
						>
							<font-awesome-icon
								role="button"
								:icon="['far', 'chevron-left']"
								class="back"
								aria-label="Click to go back"
							/>
						</button>
						<span
							class="modal-title"
							data-test="modal-title"
							role="heading"
							:title="
								truncateModalFields({
									fieldName: modalTitle,
									fieldLength: 36,
									attributeName: 'title',
								})
							"
						>
							{{
								truncateModalFields({ fieldName: modalTitle, fieldLength: 36 })
							}}
						</span>
						<button
							class="b360-modal-close-btn"
							data-test="close-button"
							aria-label="Close Modal"
							type="button"
							@click="$emit('close')"
						>
							<font-awesome-icon :icon="['far', 'times-circle']" />
						</button>
					</header>
					<section class="modal-content">
						<div class="overlay" v-show="waiting"></div>
						<slot name="modalContent" />
					</section>
					<footer class="modal-footer">
						<div class="alt-action-btns">
							<QuickActionsMenu
								v-if="showQuickAction"
								isMultiple
								darkButton
								:buttonText="`Download a Report Template`"
								:menus="[downloadButtons]"
								class="download-button"
								alignLeft
								@handleCalyxReportDownload="$emit('handleCalyxReportDownload')"
								@handleExcelDownload="$emit('handleExcelDownload')"
							/>
							<button
								v-for="(button, bindex) in altButtons"
								v-show="!waiting"
								:key="bindex"
								class="b360-btn b360-modal-action-btn"
								:class="button.class"
								data-test="alt-action-btns"
								@click="$emit(button.emitAction)"
								:disabled="button.disabled"
								type="button"
							>
								{{ button.caption }}
							</button>
						</div>
						<div class="user-action-btns">
							<button
								v-show="!waiting"
								v-if="cancelBtnName"
								class="b360-btn b360-modal-cancel-btn"
								data-test="cancel-alt-button"
								@click="$emit('close')"
								aria-label="Cancel to Close Modal"
								type="button"
							>
								{{ cancelBtnName }}
							</button>
							<button
								v-show="!waiting && cancel"
								v-else
								class="b360-btn b360-modal-cancel-btn"
								data-test="cancel-button"
								ref="cancelButton"
								@click="$emit('close')"
								aria-label="Cancel to Close Modal"
								type="button"
							>
								Cancel
							</button>
							<button
								class="b360-btn b360-modal-action-btn"
								:class="{
									waiting: waiting,
									disabled: disabled,
								}"
								data-test="action-button"
								:aria-label="`click ${actionBtnName}`"
								ref="mainActionButton"
								@click="handleClick"
								v-show="actionBtnName"
								type="button"
								:disabled="disabled"
							>
								<span class="btn-text" v-show="!waiting">{{
									actionBtnName
								}}</span>
								<font-awesome-icon
									class="waiting-animation"
									:icon="['far', 'circle-notch']"
									v-show="waiting"
								/>
								<font-awesome-icon
									class="success-animation"
									:icon="['far', 'check']"
									v-show="success"
								/>
							</button>
						</div>
					</footer>
				</section>
			</section>
		</section>
	</transition>
</template>

<script>
import Vue from 'vue';
import {
	faCircleNotch,
	faCheck,
	faTimesCircle,
} from '@fortawesome/pro-regular-svg-icons/';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { truncateModalFields } from '@/views/client-connect-v2/helpers';
import QuickActionsMenu from '@/views/client-connect-v2/QuickActionsMenu';

library.add(faCircleNotch, faCheck, faTimesCircle);
Vue.component('font-awesome-icon', FontAwesomeIcon);

export default {
	name: 'modal',
	components: {
		QuickActionsMenu,
	},
	props: {
		show: Boolean,
		modalTitle: String,
		actionBtnName: String,
		cancelBtnName: String,
		altButtons: Array,
		waiting: Boolean,
		cancel: {
			default: true,
			type: Boolean,
		},
		disabled: Boolean,
		showBackCaret: {
			default: false,
			type: Boolean,
		},
		downloadButtons: Array,
		showQuickAction: {
			default: false,
			type: Boolean,
		},
	},
	data() {
		return {
			success: false,
		};
	},
	methods: {
		truncateModalFields,
		close(e) {
			if (this.show && e.code === 'Escape') this.$emit('close');
		},
		handleClick() {
			if (typeof this.waiting === 'undefined' || !this.waiting) {
				// even though this is false by default we're checking for undefined just in case it's not false
				this.$emit('clicked');
			}
		},
	},
	mounted() {
		this.$emit('init');
		document.addEventListener('keydown', this.close);
	},
};
</script>

<style lang="scss" scoped>
.global-modal {
	position: fixed;
	z-index: 9998;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba($black, 0.5);
	display: block;
	transition: opacity 0.15s linear;
	overflow-y: auto;

	&.suggested-post-modal {
		.modal-dialog {
			top: 20px;
		}

		.modal-content {
			height: calc(100vh - 200px);
			@include mobile {
				height: calc(100vh - 250px);
			}
			overflow-y: hidden;
		}
	}

	&.social-share-modal {
		.modal-dialog {
			@include mobile {
				top: 94px;
			}
		}

		.modal-content {
			height: calc(100vh - 260px);
			@include mobile {
				height: calc(100vh - 320px);
			}
			overflow-y: hidden;
		}
	}
	&.legal-modal {
		.modal-content {
			height: unset;
		}
	}
	&.delete-address-modal {
		.modal-content {
			height: unset;
		}
	}

	&.email-confirmation-modal,
	&.ios-modal {
		.modal-content {
			height: unset;
			max-height: calc(100vh - 242px);

			@include lg {
				max-height: calc(100vh - 337px);
			}
		}
	}
}

.modal-dialog {
	max-width: 1280px;
	width: 80vw;
	display: block;
	top: 90px;
	position: relative;
	margin: 0 auto;

	@include mobile {
		width: calc(100vw - 30px);
		top: 15px;
	}

	.modal-wrapper {
		background-color: $off-white;
		border-radius: 5px;
		box-shadow: 0 0 20px 0 rgba($black, 0.15);
		position: relative;
	}

	header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 30px;
		background-color: $white;
		border-radius: 5px 5px 0 0;
		box-shadow: inset 0 -1px 0 0 $border-color-light;
		padding: 15px;
		color: $text-base-color;
		@include typo--roboto__medium;
		font-size: $b360-font-size--large;

		.b360-modal-close-btn,
		.b360-modal-back-btn {
			background-color: transparent;
			border: 0;
			height: 24px;
			color: $brand-blue;
			cursor: pointer;
			font-size: $b360-font-size--larger;
		}
	}

	.modal-content {
		position: relative;
		padding: 20px;
		overflow: hidden;
		overflow-y: auto;
		height: calc(100vh - 337px);

		@include mobile {
			height: calc(100vh - 242px);
		}

		.b360-grid-row {
			margin-bottom: unset;
		}

		.overlay {
			position: absolute;
			width: 100%;
			height: 100%;
			background: rgba(253, 252, 253, 0.875);
		}
	}

	footer {
		background-color: $white;
		min-height: 45px;
		border-radius: 0 0 5px 5px;
		box-shadow: inset 0px 1px 0px 0px $border-color-light;
		padding: 15px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;

		.user-action-btns {
			display: flex;
			flex-wrap: wrap;
			margin-left: auto;

			@include mobile {
				width: 100%;
			}
		}

		.alt-action-btns {
			display: flex;
			flex-wrap: wrap;
			margin-left: 0;

			@include mobile {
				width: 100%;
			}

			button:first-child {
				margin-left: 0;
			}
		}

		.b360-modal-action-btn.waiting {
			background-color: $b360-color--gray__lightest;
			cursor: wait;
		}

		.b360-modal-action-btn.disabled {
			background-color: $background-disabled;
			color: $color-disabled;
			cursor: not-allowed;
		}

		.text-button {
			color: $brand-blue;
			font-weight: 500;
			border: none;
			background: none;
			padding: 0;
			text-align: left;
			@include rem('font-size', 16px);

			&:hover,
			&:focus,
			&:active,
			&.active {
				color: $b360-color--blue__darker;
				cursor: pointer;
			}
			&:disabled {
				color: $b360-color--gray__medium;
				cursor: not-allowed;
			}
		}
	}
}

.modal-top-fix {
	.modal-dialog {
		top: 15vw;
	}
}

.suggested-posts-container .modal-dialog .modal-content {
	padding: {
		left: 0;
		right: 0;
	}

	margin-bottom: 0;
}

.sm {
	.modal-dialog {
		max-width: 480px;
	}
}

.md {
	.modal-dialog {
		max-width: 640px;
	}
}

.lg {
	.modal-dialog {
		max-width: 920px;
	}
}

.disable-padding {
	.modal-content {
		margin-bottom: 0;
		@include rem(padding, 20px 0 0 0);
	}
}

.disable-cancel {
	.b360-modal-cancel-btn {
		display: none;
	}
}

.b360-grid-row {
	margin-bottom: unset;
}

.modal-enter,
.modal-leave-active {
	opacity: 0;
}

.modal-enter .modal-dialog,
.modal-leave-active .modal-dialog {
	transform: translate(0, -50px);
	opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.5s ease;
}

.modal-enter-active .modal-dialog,
.modal-leave-active .modal-dialog {
	transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}
</style>
